import styled from 'styled-components';

import { IconButton } from '@components/icon-button';
import { IconCloseRegular } from '@components/icons/close-regular';
import { SIDEBAR_DEFAULT_Z_INDEX } from '@components/sidebar';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { Keys } from '@components/types/keyboard';
import { useEscapeLayer } from '@hooks/use-escape-layer';
import { useKeyPress } from '@hooks/use-keypress';
import { useScopedTranslate } from '@locale/translate';
import { theme } from '@styles/theme';
import { pxToRem } from '@utils/converters';

import { PreviewEmailProps } from '../send-email/email-preview';
import { EmailPreviewContent } from '../send-email/email-preview-modal';

export type EmailPreviewPanelProps = PreviewEmailProps & { onClose(): void };

// A floating, backdrop-less live preview of the notification email -- rendered
// inside the sidebar's own React tree (not via showModal/useModal) so it can
// re-render as the sidebar's form values and the link-type popover's draft
// change. See the `showModal`-freezes-props gotcha in
// `ui/core/components/CLAUDE.md` for why a modal can't do this.
export const EmailPreviewPanel = ({ onClose, ...previewProps }: EmailPreviewPanelProps) => {
  const { t } = useScopedTranslate(locale);

  // Without this the sidebar's own Escape handler fires on the same keypress and
  // closes the sidebar underneath us.
  useEscapeLayer();
  useKeyPress({ key: Keys.Escape, handler: onClose });

  return (
    <Container data-testid="form-email-preview-panel" role="dialog" aria-label={t('emailPreview')}>
      <Stack orientation="horizontal" align="center" justify="space-between">
        <Text font="heading2" text={t('emailPreview')} />
        <IconButton
          variant="ghost-purple"
          icon={<IconCloseRegular />}
          dataTestid="form-email-preview-close"
          onClick={onClose}
        />
      </Stack>
      <EmailPreviewContent {...previewProps} />
    </Container>
  );
};

// The panel floats to the left of the sidebar (`@components/sidebar`), so its
// horizontal placement is derived from the sidebar's own width instead of two
// independently-guessed pixel counts that could drift apart.
const SIDEBAR_WIDTH_PX = 472;
const PANEL_SIDEBAR_GAP_PX = 24; // breathing room between the panel and the sidebar
const PANEL_MIN_LEFT_MARGIN_PX = 44; // keeps the panel off the left edge of the viewport
const PANEL_RIGHT_PX = SIDEBAR_WIDTH_PX + PANEL_SIDEBAR_GAP_PX;
const PANEL_MAX_WIDTH_SUBTRAHEND_PX = PANEL_RIGHT_PX + PANEL_MIN_LEFT_MARGIN_PX;

const PANEL_TOP_PX = 96;
const PANEL_WIDTH_PX = 728;

// The panel must sit one step above the sidebar so it overlays it without a
// backdrop and without trapping focus (the sidebar stays editable while the
// panel is open).
// +2, not +1: the sidebar's own AnimationContainer already renders at
// SIDEBAR_DEFAULT_Z_INDEX + 1, so +1 would only tie with it and leave the panel
// relying on DOM order to paint on top.
const PANEL_Z_INDEX = SIDEBAR_DEFAULT_Z_INDEX + 2;

const Container = styled.div({
  position: 'fixed',
  top: pxToRem(PANEL_TOP_PX),
  right: pxToRem(PANEL_RIGHT_PX),
  width: pxToRem(PANEL_WIDTH_PX),
  maxWidth: `calc(100vw - ${pxToRem(PANEL_MAX_WIDTH_SUBTRAHEND_PX)})`,
  maxHeight: '80vh',
  overflowY: 'auto',
  backgroundColor: theme.color.backgroundLightDark,
  borderRadius: pxToRem(16),
  boxShadow: theme.shadow.high,
  padding: pxToRem(24),
  zIndex: PANEL_Z_INDEX,
});

const locale = {
  en: {
    emailPreview: 'Email preview',
  },
};
